import { Switch, Route, Redirect, useLocation } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ThemeProvider } from "@/components/ThemeProvider";
import { useAuth } from "@/hooks/useAuth";
import { useEffect } from "react";
import { flushDeviceTokenAfterAuth } from "@/lib/native";
import {
  canSell,
  getDefaultRouteForUser,
  isOnboardingComplete,
  isEmailVerified,
} from "@/lib/mode";
import NotFound from "@/pages/not-found";
import Landing from "@/pages/Landing";
import { showsGuestFeed } from "@/hooks/useGuestBrowsing";
import BrowseHome from "@/pages/BrowseHome";
import SellerDashboard from "@/pages/SellerDashboard";
import SellerSetup from "@/pages/SellerSetup";
import SellerCompliance from "@/pages/SellerCompliance";
import SellerMeatGuidance from "@/pages/SellerMeatGuidance";
import SellerPayouts from "@/pages/SellerPayouts";
import Checkout from "@/pages/Checkout";
import DemoEbt from "@/pages/DemoEbt";
import OrderConfirmation from "@/pages/OrderConfirmation";
import OrderDetail from "@/pages/OrderDetail";
import BuyerOrders from "@/pages/BuyerOrders";
import ActiveOrders from "@/pages/ActiveOrders";
import FeedbackThreadPage from "@/pages/FeedbackThreadPage";
import Notifications from "@/pages/Notifications";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEmail from "@/pages/VerifyEmail";
import CheckEmail from "@/pages/CheckEmail";
import Onboarding from "@/pages/Onboarding";
import Account from "@/pages/Account";
import Profile from "@/pages/Profile";
import ProductDetail from "@/pages/ProductDetail";
import RecipePicker from "@/pages/RecipePicker";
import BoxPreview from "@/pages/BoxPreview";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminFeedback from "@/pages/AdminFeedback";
import AdminMessages from "@/pages/AdminMessages";
import AdminUsers from "@/pages/AdminUsers";
import AdminDemand from "@/pages/AdminDemand";
import AdminMakers from "@/pages/AdminMakers";
import AdminTriage from "@/pages/AdminTriage";
import AdminDeliveryDeclines from "@/pages/AdminDeliveryDeclines";
import AdminDemoControl from "@/pages/AdminDemoControl";
import Deliveries from "@/pages/Deliveries";
import Messages from "@/pages/Messages";
import Privacy from "@/pages/Privacy";
import Support from "@/pages/Support";
import Terms from "@/pages/Terms";
import TermsOfUse from "@/pages/TermsOfUse";
import { LegalUpdateGate } from "@/components/LegalUpdateGate";
import { HeatPromptGate } from "@/components/HeatPromptGate";
import BlockedAccounts from "@/pages/BlockedAccounts";
import AdminReports from "@/pages/AdminReports";
import EventClaim from "@/pages/EventClaim";
import EventVerify from "@/pages/EventVerify";
import Garden from "@/pages/Garden";
import GardenSetup from "@/pages/GardenSetup";
import GardenDiagnose from "@/pages/GardenDiagnose";
import GardenJournal from "@/pages/GardenJournal";
import GardenJournalEntry from "@/pages/GardenJournalEntry";
import Upgrade from "@/pages/Upgrade";
import GardenComingSoon from "@/pages/GardenComingSoon";
import AuthGoogleCallback from "@/pages/AuthGoogleCallback";
import ReserveCheckout from "@/pages/ReserveCheckout";
import Reservations from "@/pages/Reservations";
import NotificationSettings from "@/pages/NotificationSettings";
import AccountRewards from "@/pages/AccountRewards";
import SellerGrowthPhotos from "@/pages/SellerGrowthPhotos";
import SellerExpenses from "@/pages/SellerExpenses";
import Barter from "@/pages/Barter";
import SellerStorefront from "@/pages/SellerStorefront";
import { FeedbackWidget } from "@/components/FeedbackWidget";
import { TripStatusBar } from "@/components/TripStatusBar";
import { AdminQuickAccess } from "@/components/AdminQuickAccess";
import { HandshakeBanners } from "@/components/HandshakeBanners";
import { ReviewPromptBanner } from "@/components/ReviewPromptBanner";
import { MissionsWidget } from "@/components/MissionsWidget";
import { MissionsEngine } from "@/components/MissionsEngine";
import { useQuery } from "@tanstack/react-query";

function MissionsHost() {
  const { user } = useAuth();
  const { data: config } = useQuery<{ demoMode: boolean }>({
    queryKey: ["/api/public/config"],
  });

  const enabled =
    Number((user as any)?.isBetaTester ?? 0) === 1 && Boolean(config?.demoMode);

  if (!enabled) return null;

  return (
    <>
      <MissionsEngine />
      <MissionsWidget />
    </>
  );
}
import { BottomNav } from "@/components/BottomNav";
import { ListingComposerChip } from "@/components/ListingComposerChip";
import { OfflineBanner } from "@/components/OfflineBanner";
import { MapToggle } from "@/components/MapToggle";
import { NativeVersionGate } from "@/components/NativeVersionGate";
import { PermissionsGate } from "@/components/PermissionsGate";
import { useNavTrailRecorder } from "@/hooks/useNavTrailRecorder";
import { useMobileWebGate } from "@/hooks/useMobileWebGate";
import {
  initErrorCapture,
  initSessionExpiryWatcher,
  initFreezeDetector,
  SESSION_EXPIRED_EVENT,
} from "@/lib/errorTrail";
import { initAnalytics, trackPageView } from "@/lib/analytics";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "@/hooks/use-toast";
import { captureReferralFromUrl, hydrateReferralFromConfig, applyStoredReferralCodeAfterAuth } from "@/lib/referral";

initErrorCapture();
initSessionExpiryWatcher();
initFreezeDetector();
captureReferralFromUrl();
void hydrateReferralFromConfig();
initAnalytics();

function SessionExpiryWatcher() {
  const queryClient = useQueryClient();

  useEffect(() => {
    let reloaded = false;
    const handler = () => {
      const cached = queryClient.getQueryData(["/api/auth/user"]);
      if (!cached) return;
      queryClient.setQueryData(["/api/auth/user"], null);
      toast({
        title: "Please sign in again",
        description:
          "Looks like a new version was deployed. Sign in again to load the latest changes.",
        duration: 10000,
      });
      // Without a full page reload, the WebView keeps the old JS bundle
      // loaded after re-login — the toast's "load the latest changes"
      // copy was a lie. Reload after a short beat so the toast has time
      // to paint before the DOM is replaced, and so the wrapped Capacitor
      // app actually re-fetches index.html and the new entry chunk.
      if (reloaded) return;
      reloaded = true;
      setTimeout(() => {
        try {
          window.location.reload();
        } catch {
          // ignore
        }
      }, 1200);
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, handler);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, handler);
  }, [queryClient]);

  return null;
}

function HomeOrFeed() {
  // Feed and the five browse modes are now swipeable panels behind one wrapper
  // that reads ?mode=/?feed= to pick the initial tab.
  return <BrowseHome />;
}

function Router() {
  const { user, isAuthenticated, isLoading, isAdmin } = useAuth();
  const [pathname] = useLocation();
  useNavTrailRecorder();
  useMobileWebGate();

  useEffect(() => {
    if (isAuthenticated) {
      void flushDeviceTokenAfterAuth();
      void applyStoredReferralCodeAfterAuth();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    trackPageView(pathname);
  }, [pathname]);

  // Start every new page at the top. Wouter navigates via pushState, which
  // preserves the previous scroll offset — so tapping into a product from a
  // scrolled list opened the detail page halfway down. Reset on each path
  // change (query-only changes don't update pathname, so they're unaffected).
  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    const shell = document.getElementById("app-scroll");
    if (shell) shell.scrollTop = 0;
  }, [pathname]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center text-muted-foreground">
        Loading…
      </div>
    );
  }

  const onboardingComplete = isOnboardingComplete(user);
  const emailVerified = isEmailVerified(user);
  const defaultAuthedRoute = getDefaultRouteForUser(user);

  return (
    <Switch>
      <Route path="/">
        {() => {
          if (!isAuthenticated) return showsGuestFeed() ? <HomeOrFeed /> : <Landing />;
          if (!emailVerified) return <Redirect to="/check-email" />;
          if (!onboardingComplete) return <Redirect to="/onboarding" />;
          return <HomeOrFeed />;
        }}
      </Route>

      <Route path="/login">
        {() => (isAuthenticated ? <Redirect to={defaultAuthedRoute} /> : <Login />)}
      </Route>

      <Route path="/register">
        {() => (isAuthenticated ? <Redirect to={defaultAuthedRoute} /> : <Register />)}
      </Route>

      <Route path="/forgot-password">
        {() => (isAuthenticated ? <Redirect to={defaultAuthedRoute} /> : <ForgotPassword />)}
      </Route>

      <Route path="/reset-password">
        {() => <ResetPassword />}
      </Route>

      <Route path="/verify-email">
        {() => <VerifyEmail />}
      </Route>

      <Route path="/check-email">
        {() => {
          if (!isAuthenticated) return <Redirect to="/login" />;
          if (emailVerified) return <Redirect to={defaultAuthedRoute} />;
          return <CheckEmail />;
        }}
      </Route>

      <Route path="/auth/google-callback">
        {() => <AuthGoogleCallback />}
      </Route>

      <Route path="/onboarding">
        {() => {
          if (!isAuthenticated) return <Redirect to="/login" />;
          if (!emailVerified) return <Redirect to="/check-email" />;
          if (onboardingComplete) return <Redirect to={defaultAuthedRoute} />;
          return <Onboarding />;
        }}
      </Route>

      <Route path="/account">
        {() => {
          if (!isAuthenticated) return <Redirect to="/login" />;
          if (!onboardingComplete) return <Redirect to="/onboarding" />;
          return <Account />;
        }}
      </Route>

      <Route path="/seller/setup">
        {() => {
          if (!isAuthenticated) return <Redirect to="/login" />;
          if (!onboardingComplete) return <Redirect to="/onboarding" />;
          if (!canSell(user)) return <Redirect to="/" />;
          return (
            <PermissionsGate
              requirePush
              title="Enable notifications to sell"
              description="Sellers need push notifications so you don't miss new orders or messages from buyers."
            >
              <SellerSetup />
            </PermissionsGate>
          );
        }}
      </Route>

      <Route path="/seller/compliance">
        {() => {
          if (!isAuthenticated) return <Redirect to="/login" />;
          if (!onboardingComplete) return <Redirect to="/onboarding" />;
          if (!canSell(user)) return <Redirect to="/" />;
          return <SellerCompliance />;
        }}
      </Route>

      <Route path="/seller/meat-guidance">
        {() => {
          if (!isAuthenticated) return <Redirect to="/login" />;
          if (!onboardingComplete) return <Redirect to="/onboarding" />;
          if (!canSell(user)) return <Redirect to="/" />;
          return <SellerMeatGuidance />;
        }}
      </Route>

      <Route path="/seller/payouts">
        {() => {
          if (!isAuthenticated) return <Redirect to="/login" />;
          if (!onboardingComplete) return <Redirect to="/onboarding" />;
          if (!canSell(user)) return <Redirect to="/" />;
          return <SellerPayouts />;
        }}
      </Route>

      <Route path="/seller">
        {() => {
          if (!isAuthenticated) return <Redirect to="/login" />;
          if (!onboardingComplete) return <Redirect to="/onboarding" />;
          if (!canSell(user)) return <Redirect to="/" />;
          if (!user?.displayName) return <Redirect to="/seller/setup" />;
          return (
            <PermissionsGate
              requirePush
              title="Enable notifications to sell"
              description="Sellers need push notifications so you don't miss new orders or messages from buyers."
            >
              <SellerDashboard />
            </PermissionsGate>
          );
        }}
      </Route>

      <Route path="/checkout">
        {() => {
          if (!isAuthenticated) return <Redirect to="/login" />;
          if (!onboardingComplete) return <Redirect to="/onboarding" />;
          return <Checkout />;
        }}
      </Route>

      <Route path="/order-confirmation">
        {() => {
          if (!isAuthenticated) return <Redirect to="/login" />;
          if (!onboardingComplete) return <Redirect to="/onboarding" />;
          return <OrderConfirmation />;
        }}
      </Route>

      <Route path="/saved">
        {() => <Redirect to="/?browse=saved" />}
      </Route>

      <Route path="/active">
        {() => {
          if (!isAuthenticated) return <Redirect to="/login" />;
          if (!onboardingComplete) return <Redirect to="/onboarding" />;
          return <ActiveOrders />;
        }}
      </Route>

      <Route path="/feedback/:id">
        {() => {
          if (!isAuthenticated) return <Redirect to="/login" />;
          return <FeedbackThreadPage />;
        }}
      </Route>

      <Route path="/orders">
        {() => {
          if (!isAuthenticated) return <Redirect to="/login" />;
          if (!onboardingComplete) return <Redirect to="/onboarding" />;
          return <BuyerOrders />;
        }}
      </Route>

      <Route path="/orders/:id">
        {() => {
          if (!isAuthenticated) return <Redirect to="/login" />;
          if (!onboardingComplete) return <Redirect to="/onboarding" />;
          return <OrderDetail />;
        }}
      </Route>

      <Route path="/reservations">
        {() => {
          if (!isAuthenticated) return <Redirect to="/login" />;
          if (!onboardingComplete) return <Redirect to="/onboarding" />;
          return <Reservations />;
        }}
      </Route>

      <Route path="/reserve/:itemId">
        {() => {
          if (!isAuthenticated) return <Redirect to="/login" />;
          if (!onboardingComplete) return <Redirect to="/onboarding" />;
          return <ReserveCheckout />;
        }}
      </Route>

      <Route path="/account/notifications">
        {() => {
          if (!isAuthenticated) return <Redirect to="/login" />;
          if (!onboardingComplete) return <Redirect to="/onboarding" />;
          return <NotificationSettings />;
        }}
      </Route>

      <Route path="/account/rewards">
        {() => {
          if (!isAuthenticated) return <Redirect to="/login" />;
          if (!onboardingComplete) return <Redirect to="/onboarding" />;
          return <AccountRewards />;
        }}
      </Route>

      <Route path="/seller/growth-photos">
        {() => {
          if (!isAuthenticated) return <Redirect to="/login" />;
          if (!onboardingComplete) return <Redirect to="/onboarding" />;
          if (!canSell(user)) return <Redirect to="/" />;
          return <SellerGrowthPhotos />;
        }}
      </Route>

      <Route path="/seller/expenses">
        {() => {
          if (!isAuthenticated) return <Redirect to="/login" />;
          if (!onboardingComplete) return <Redirect to="/onboarding" />;
          if (!canSell(user)) return <Redirect to="/" />;
          return <SellerExpenses />;
        }}
      </Route>

      <Route path="/notifications">
        {() => {
          if (!isAuthenticated) return <Redirect to="/login" />;
          if (!onboardingComplete) return <Redirect to="/onboarding" />;
          return <Notifications />;
        }}
      </Route>

      <Route path="/barter">
        {() => {
          if (!isAuthenticated) return <Redirect to="/login" />;
          if (!onboardingComplete) return <Redirect to="/onboarding" />;
          return <Barter />;
        }}
      </Route>

      <Route path="/items/:id">
        {() => {
          // Public so storefront guests (no login) can view full product
          // details. Logged-in but un-onboarded users still finish onboarding.
          if (isAuthenticated && !onboardingComplete) return <Redirect to="/onboarding" />;
          return <ProductDetail />;
        }}
      </Route>

      <Route path="/recipes">
        {() => {
          if (!isAuthenticated) return <Redirect to="/login" />;
          if (!onboardingComplete) return <Redirect to="/onboarding" />;
          return <RecipePicker />;
        }}
      </Route>

      <Route path="/box-preview">
        {() => {
          if (!isAuthenticated) return <Redirect to="/login" />;
          if (!onboardingComplete) return <Redirect to="/onboarding" />;
          return <BoxPreview />;
        }}
      </Route>

      <Route path="/u/:id">
        {() => <Profile />}
      </Route>

      <Route path="/sellers">
        {() => <Redirect to="/?browse=producers" />}
      </Route>

      <Route path="/deliveries">
        {() => {
          if (!isAuthenticated) return <Redirect to="/login" />;
          return <Deliveries />;
        }}
      </Route>

      <Route path="/messages">
        {() => {
          if (!isAuthenticated) return <Redirect to="/login" />;
          return <Messages />;
        }}
      </Route>

      {import.meta.env.DEV && (
        <Route path="/demo/ebt">
          {() => <DemoEbt />}
        </Route>
      )}

      <Route path="/privacy">
        {() => <Privacy />}
      </Route>

      <Route path="/support">
        {() => <Support />}
      </Route>

      <Route path="/terms">
        {() => <Terms />}
      </Route>

      <Route path="/terms-of-use">
        {() => <TermsOfUse />}
      </Route>

      <Route path="/event/verify/:token">
        {() => <EventVerify />}
      </Route>

      <Route path="/event/:slug">
        {() => <EventClaim />}
      </Route>

      <Route path="/garden">
        {() => {
          if (!isAuthenticated) return <Redirect to="/login" />;
          if (!onboardingComplete) return <Redirect to="/onboarding" />;
          if (!isAdmin) return <GardenComingSoon />;
          return <Garden />;
        }}
      </Route>

      <Route path="/garden/setup">
        {() => {
          if (!isAuthenticated) return <Redirect to="/login" />;
          if (!onboardingComplete) return <Redirect to="/onboarding" />;
          if (!isAdmin) return <GardenComingSoon />;
          return <GardenSetup />;
        }}
      </Route>

      <Route path="/garden/diagnose">
        {() => {
          if (!isAuthenticated) return <Redirect to="/login" />;
          if (!onboardingComplete) return <Redirect to="/onboarding" />;
          if (!isAdmin) return <GardenComingSoon />;
          return <GardenDiagnose />;
        }}
      </Route>

      <Route path="/garden/journal">
        {() => {
          if (!isAuthenticated) return <Redirect to="/login" />;
          if (!onboardingComplete) return <Redirect to="/onboarding" />;
          if (!isAdmin) return <GardenComingSoon />;
          return <GardenJournal />;
        }}
      </Route>

      <Route path="/garden/journal/:id">
        {() => {
          if (!isAuthenticated) return <Redirect to="/login" />;
          if (!onboardingComplete) return <Redirect to="/onboarding" />;
          if (!isAdmin) return <GardenComingSoon />;
          return <GardenJournalEntry />;
        }}
      </Route>

      <Route path="/upgrade">
        {() => {
          if (!isAuthenticated) return <Redirect to="/login" />;
          return <Upgrade />;
        }}
      </Route>

      <Route path="/buy/:handle">
        {() => <SellerStorefront />}
      </Route>

      <Route path="/admin">
        {() => {
          if (!isAuthenticated) return <Redirect to="/login" />;
          if (!isAdmin) return <Redirect to="/" />;
          return <AdminDashboard />;
        }}
      </Route>

      <Route path="/admin/dashboard">
        {() => {
          if (!isAuthenticated) return <Redirect to="/login" />;
          if (!isAdmin) return <Redirect to="/" />;
          return <AdminDashboard />;
        }}
      </Route>

      <Route path="/admin/reports">
        {() => {
          if (!isAuthenticated) return <Redirect to="/login" />;
          if (!isAdmin) return <Redirect to="/" />;
          return <AdminReports />;
        }}
      </Route>

      <Route path="/admin/makers">
        {() => {
          if (!isAuthenticated) return <Redirect to="/login" />;
          if (!isAdmin) return <Redirect to="/" />;
          return <AdminMakers />;
        }}
      </Route>

      <Route path="/account/blocked">
        {() => {
          if (!isAuthenticated) return <Redirect to="/login" />;
          return <BlockedAccounts />;
        }}
      </Route>

      <Route path="/admin/feedback">
        {() => {
          if (!isAuthenticated) return <Redirect to="/login" />;
          return <AdminFeedback />;
        }}
      </Route>

      <Route path="/admin/messages">
        {() => {
          if (!isAuthenticated) return <Redirect to="/login" />;
          if (!isAdmin) return <Redirect to="/" />;
          return <AdminMessages />;
        }}
      </Route>

      <Route path="/admin/users">
        {() => {
          if (!isAuthenticated) return <Redirect to="/login" />;
          if (!isAdmin) return <Redirect to="/" />;
          return <AdminUsers />;
        }}
      </Route>

      <Route path="/admin/demand">
        {() => {
          if (!isAuthenticated) return <Redirect to="/login" />;
          if (!isAdmin) return <Redirect to="/" />;
          return <AdminDemand />;
        }}
      </Route>

      <Route path="/admin/triage/:id?">
        {(params: { id?: string }) => {
          if (!isAuthenticated) return <Redirect to="/login" />;
          return <AdminTriage runId={params.id} />;
        }}
      </Route>

      <Route path="/admin/delivery-declines">
        {() => {
          if (!isAuthenticated) return <Redirect to="/login" />;
          return <AdminDeliveryDeclines />;
        }}
      </Route>

      <Route path="/admin/demo-control">
        {() => {
          if (!isAuthenticated) return <Redirect to="/login" />;
          return <AdminDemoControl />;
        }}
      </Route>

      <Route component={NotFound} />
    </Switch>
  );
}

// The QR express storefront (/buy/:handle) is a standalone guest page. Rendering
// it inside the app chrome (BottomNav, overlays, paddingBottom) collides with its
// own fixed checkout bar and stacks overlays on top — "glitched out" for an
// authenticated user. Suppress all chrome on storefront routes.
function AppChrome() {
  const [location] = useLocation();
  const bare = location.startsWith("/buy/") || location.startsWith("/event/");

  // Scroll the shell, not the document. Android's WebView draws the overscroll
  // stretch for the ROOT scroller at the native view layer, where CSS
  // `overscroll-behavior: none` doesn't reach it — so every page that scrolled
  // the document rubber-banded the whole surface, fixed top and bottom bars
  // included. A non-root scroller does honour `overscroll-behavior`, which is
  // why the browse pager (fixed shell, panels scroll internally) never had it.
  // Guest storefronts render bare and keep the document scroller.
  useEffect(() => {
    if (bare) return;
    document.documentElement.classList.add("stoop-app-shell");
    return () => document.documentElement.classList.remove("stoop-app-shell");
  }, [bare]);

  if (bare) {
    return <Router />;
  }

  if (import.meta.env.DEV && location.startsWith("/demo/")) {
    return <Router />;
  }

  return (
    <>
      {/* BottomNav is 52px of content PLUS env(safe-area-inset-bottom), which on
          a phone with a home indicator is another ~34px. Reserving a flat 52
          left the last row of any long page hidden behind the nav on iOS.
          The banners live inside the scroller so they keep sticking to the top
          of the scrollport rather than to a viewport that no longer scrolls. */}
      <div id="app-scroll" style={{ paddingBottom: "calc(52px + env(safe-area-inset-bottom, 0px))" }}>
        <OfflineBanner />
        <HandshakeBanners />
        <ReviewPromptBanner />
        <Router />
      </div>
      {import.meta.env.DEV && (
        <button
          onClick={() => window.location.assign("/demo/ebt")}
          style={{ bottom: "calc(60px + env(safe-area-inset-bottom, 0px))" }}
          className="fixed left-3 z-50 rounded-full bg-emerald-700 px-3 py-1.5 text-xs font-semibold text-white shadow-lg"
        >
          EBT demo
        </button>
      )}
      <FeedbackWidget />
      <AdminQuickAccess />
      <MissionsHost />
      <TripStatusBar />
      <MapToggle />
      <ListingComposerChip />
      <BottomNav />
      <LegalUpdateGate />
      <HeatPromptGate />
    </>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider defaultTheme="system" storageKey="marketplace-theme">
        <TooltipProvider>
          <Toaster />
          <SessionExpiryWatcher />
          <NativeVersionGate>
            <AppChrome />
          </NativeVersionGate>
        </TooltipProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
